import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { ChevronRight, Lightbulb } from "lucide-react";
import type { InsightAction, InsightDecision } from "../core";
import { useRuntime } from "../react/runtime/RuntimeContext";
import { confirm } from "./ConfirmDialog";

// CockpitSuggestions — the cockpit <suggestions/> card (form-view revamp Enh 14 ·
// "Suggested next"). It is a PRESENTATION surface only: it consumes
// foundation.ai_insights' deterministic engine (owned by that module) —
// `insights.evaluate` for the record's recommended actions and
// `insights.runAction` to execute a confirmed one. No suggestion logic lives
// here. Each recommended action renders as a nudge colour-coded by severity
// (HIGH red · MEDIUM amber · LOW neutral) with a one-click, confirm-to-act
// button. Omits entirely when the model has no decision profile / no actions
// (or the engine isn't present on this build) — graceful degradation.

interface Props {
    modelKey: string;
    recordRef: string | null;
}

const URGENCY_CLASS: Record<string, string> = {
    HIGH: "cockpit-suggestion--red",
    MEDIUM: "cockpit-suggestion--amber",
    LOW: "cockpit-suggestion--neutral",
};

export function CockpitSuggestions({ modelKey, recordRef }: Props): ReactNode {
    const runtime = useRuntime();
    const [decision, setDecision] = useState<InsightDecision | null>(null);
    const [loading, setLoading] = useState(true);
    const [busyRef, setBusyRef] = useState<string | null>(null);
    const [epoch, setEpoch] = useState(0);

    useEffect(() => {
        if (!recordRef) {
            setLoading(false);
            return;
        }
        let alive = true;
        void (async () => {
            setLoading(true);
            try {
                const res = await runtime.services.insights.evaluate(modelKey, recordRef);
                if (alive) setDecision(res);
            } catch {
                if (alive) setDecision(null);
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => {
            alive = false;
        };
    }, [runtime, modelKey, recordRef, epoch]);

    const actions: InsightAction[] = decision?.actions ?? [];
    if (!recordRef) return null;
    if (!loading && actions.length === 0) return null; // nothing to suggest → omit

    async function run(action: InsightAction): Promise<void> {
        if (!recordRef || !action.action_kind || !action.ref) return;
        if (action.requires_confirm) {
            const ok = await confirm({
                title: action.label,
                message: `Run "${action.label}" on this record?`,
                confirmLabel: "Confirm",
                variant: action.severity === "HIGH" ? "danger" : "default",
            });
            if (!ok) return;
        }
        setBusyRef(action.ref);
        try {
            await runtime.services.insights.runAction(modelKey, recordRef, action.action_kind, action.ref);
            setEpoch((e) => e + 1); // re-evaluate — the acted-on nudge may clear
        } catch {
            /* surfaced by the transport error toast; leave the card as-is */
        } finally {
            setBusyRef(null);
        }
    }

    return (
        <div className="cockpit-card cockpit-card--suggestions">
            <div className="cockpit-card__head">
                <Lightbulb size={13} />
                Suggested next
            </div>
            <div className="cockpit-card__body">
                {loading ? (
                    <p className="cockpit-suggestions__loading">Checking…</p>
                ) : (
                    <ul className="cockpit-suggestions__list">
                        {actions.map((a, i) => (
                            <li
                                key={`${a.ref ?? a.action_kind ?? "s"}-${i}`}
                                className={`cockpit-suggestion ${URGENCY_CLASS[a.severity] ?? "cockpit-suggestion--neutral"}`}
                            >
                                <span className="cockpit-suggestion__dot" />
                                <span className="cockpit-suggestion__text">{a.label}</span>
                                {a.action_kind && a.ref ? (
                                    <button
                                        type="button"
                                        className="cockpit-suggestion__act"
                                        disabled={busyRef === a.ref}
                                        onClick={() => void run(a)}
                                    >
                                        {busyRef === a.ref ? "…" : <ChevronRight size={13} />}
                                    </button>
                                ) : null}
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}
